// File: frontend/src/hooks/usePins.ts

import { useEffect, useState } from "react";
import { pinStore } from "../services/pinService";
import { PublicPinModel, PinTargetType } from "../models/PinModel";

/**
 * Subscribes to the shared pin store (services/pinService.ts).
 * Triggers the initial GET /api/pins fetch on first mount and re-renders the
 * consumer whenever pins change (e.g. after an admin pin/unpin action).
 */
export const usePins = () => {
  const [pins, setPins] = useState<PublicPinModel[]>(pinStore.getPins());

  useEffect(() => {
    const unsubscribe = pinStore.subscribe(() => setPins([...pinStore.getPins()]));
    // Kick off (or join) the initial fetch
    pinStore.ensureLoaded().then((p) => setPins([...p]));
    return unsubscribe;
  }, []);

  /** The pinned-topic id for a domain, or null when nothing is pinned there. */
  const getPinnedTopicId = (type: PinTargetType): number | null => {
    const pin = pins.find((p) => p.targetType === type && p.topicPinned);
    return pin ? pin.targetId : null;
  };

  /** The pinned-entry id for a specific topic, or null. */
  const getPinnedEntryId = (type: PinTargetType, targetId: number): number | null => {
    const pin = pins.find((p) => p.targetType === type && p.targetId === targetId);
    return pin?.pinnedEntryId ?? null;
  };

  return { pins, getPinnedTopicId, getPinnedEntryId };
};

export default usePins;